import {
    AdditiveBlending,
    AgXToneMapping,
    BufferGeometry,
    Camera,
    CanvasTexture,
    EquirectangularReflectionMapping,
    Float32BufferAttribute,
    LinearFilter,
    LinearSRGBColorSpace,
    Mesh,
    BoxGeometry,
    MeshBasicMaterial,
    Color,
    CameraHelper,
    AxesHelper,
    NearestFilter,
    PMREMGenerator,
    PerspectiveCamera,
    PlaneGeometry,
    RawShaderMaterial,
    RepeatWrapping,
    Scene,
    TextureLoader,
    Vector2,
    Vector3,
    WebGLRenderTarget,
    WebGLRenderer,
    Spherical,
} from "three";
import { gltfLoader, disposeRenderer } from "../utils/3d";
import { EXRLoader, WebGL } from "three/examples/jsm/Addons.js";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import gsap from "gsap";

function computeEnvmap(renderer, texture) {
    texture.mapping = EquirectangularReflectionMapping;
    const pmremGenerator = new PMREMGenerator(renderer);
    pmremGenerator.compileEquirectangularShader();

    const pmrem = pmremGenerator.fromEquirectangular(texture).texture;

    texture.dispose();
    pmremGenerator.dispose();

    return pmrem;
}

const BLUR_DPR = 0.5;

class TheRing {
    #baseFOV = 45;
    #diamondsFront;
    #events;
    #uniforms;
    #dt;
    #et;
    #viewport;
    #scene;
    #renderer;
    #orbitCamera;
    #debugCamera;
    #renderCamera;
    #camera;
    #controls;
    #mouse;
    #lerpedMouse;
    #rt;
    #brt;
    #brt1;
    #brt2;
    #quad;
    #diamondsBack;
    #glowMaterial;
    #brt1Quad;
    #brt2Quad;
    #cameraSpherical;
    #launchSpherical;
    #scrollProgress;

    constructor(wrapper) {
        this.$wrapper = wrapper;
        this.#events = new Map();

        this.#uniforms = {
            uTime: { value: 0 },
            uNoise: { value: null },
            uNoiseIntensity: { value: 0.3 },
            uResolution: { value: new Vector2(1, 1) },
            uGlobalLuminosity: { value: 1 },
        };

        this.#generateNoise();

        this.#dt = 0;
        this.#et = 0;

        this.#scrollProgress = 0;

        this.ringPosition = new Vector3();

        this.#viewport = { width: 375, height: 610, ratio: 1, dpr: 1 };

        this.#scene = new Scene();
        this.#scene.background = new Color(0x000000);
        this.#renderer = new WebGLRenderer({
            alpha: false,
        });
        this.#renderer.toneMapping = AgXToneMapping;

        this.#orbitCamera = new PerspectiveCamera(45, 1, 0.1, 10);
        this.#orbitCamera.position.setFromSpherical(
            new Spherical(0.66, Math.PI / 2, 0)
        );
        this.#scene.add(this.#orbitCamera);

        this.#camera = new PerspectiveCamera(45, 1, 0.1, 10);
        this.#orbitCamera.add(this.#camera);

        // DEBUG HELPERS
        // ==========================================================================
        {
            // const axesHelper = new AxesHelper(1);
            // this.#scene.add(axesHelper);
            // this.#debugCamera = new PerspectiveCamera(45, 1, 0.1, 10);
            // this.#debugCamera.position.setFromSpherical(new Spherical(1, 0.75, 0.75));
            // this.#debugCamera.lookAt(0,0,0)
            // const orbitCameraHelper = new CameraHelper(this.#orbitCamera);
            // this.#scene.add( orbitCameraHelper );
            // const orbitOrigin = new Mesh(new BoxGeometry(.01,.01,.01,1,1,1), new MeshBasicMaterial({color: 0x00ff00}))
            // this.#orbitCamera.add(orbitOrigin)
            // const orbitAxesHelper = new AxesHelper(.05);
            // this.#orbitCamera.add(orbitAxesHelper);
            // const cameraHelper = new CameraHelper(this.#camera);
            // cameraHelper.setColors(new Color(0x0000ff),new Color(0x0000ff),new Color(0x0000ff),new Color(0x0000ff),new Color(0x0000ff))
            // this.#scene.add( cameraHelper );
        }

        this.#renderCamera = this.#camera;

        this.#controls = new OrbitControls(
            this.#orbitCamera,
            this.#renderer.domElement
        );
        this.#controls.minDistance = 0.5;
        this.#controls.autoRotate = true;
        this.#controls.enableDamping = true;
        this.#controls.enablePan = false;
        this.#controls.enableZoom = false;

        this.$wrapper.prepend(this.#renderer.domElement);

        this.trigonometricCameraPosition = { theta: 0, phi: 0, radius: 0 };
        this.ringRotation = { theta: 0, phi: 0 };

        this.#mouse = new Vector2(0, 0);
        this.#lerpedMouse = new Vector2(0, 0);
        this.parallaxIntensity = 1;

        this.#rt = new WebGLRenderTarget(
            this.#viewport.width,
            this.#viewport.height,
            {
                samples: 1,
            }
        );
        this.#brt = new WebGLRenderTarget(
            this.#viewport.width * BLUR_DPR,
            this.#viewport.height * BLUR_DPR,
            { samples: 1 }
        );
        this.#brt1 = new WebGLRenderTarget(
            this.#viewport.width * BLUR_DPR,
            this.#viewport.height * BLUR_DPR,
            { samples: 1 }
        );
        this.#brt2 = new WebGLRenderTarget(
            this.#viewport.width * BLUR_DPR,
            this.#viewport.height * BLUR_DPR,
            { samples: 1 }
        );
        this.#brt.filter = NearestFilter;
        this.#brt1.filter = NearestFilter;
        this.#brt2.filter = NearestFilter;

        this.#quad = new Mesh(
            new BufferGeometry()
                .setAttribute(
                    "position",
                    new Float32BufferAttribute(
                        [-1, 3, 0, -1, -1, 0, 3, -1, 0],
                        3
                    )
                )
                .setAttribute(
                    "uv",
                    new Float32BufferAttribute([0, 2, 0, 0, 2, 0], 2)
                )
        );
        this.#quad.material = new RawShaderMaterial({
            vertexShader: /* glsl */ `
        precision highp float;
        attribute vec3 position;
        attribute vec2 uv;

        varying vec2 vUv;

        void main() {
          vUv = uv;
          gl_Position = vec4(position, 1.);
        }
      `,
            fragmentShader: /* glsl */ `
        precision highp float;
        varying vec2 vUv;

        uniform sampler2D uScene, uBlured, uNoise;
        uniform vec2 uResolution;
        uniform float uTime, uNoiseIntensity;

        #define PI 3.14159265

        float avg(vec3 c) { return (c.x + c.y + c.z) / 3.; }

        void main() {
          vec3 color = texture2D(uScene, vUv).rgb * 2.;
          vec4 glow = vec4(0.);

          vec3 noise = (texture2D(uNoise, vUv / 256. * uResolution + floor(uTime * 10000.) * .04).rgb - .5);
          // vec2 poisson = vec2(cos(noise.x), sin(noise.x)) / uResolution * 50.;

          glow += texture2D(uBlured, vUv);

          // color += glow.rgb * .3;
          color += vec3(glow.r * .4, glow.g * .25, glow.b * .1) * .5;
          // color = glow.rgb;
          color *= 1. + mix(vec3(avg(noise)), noise, .5) * uNoiseIntensity;

          gl_FragColor = vec4(color, 1.);
        }
      `,
            uniforms: {
                uTime: this.#uniforms.uTime,
                uResolution: this.#uniforms.uResolution,
                uScene: { value: this.#rt.texture },
                uBlured: { value: this.#brt2.texture },
                uNoise: this.#uniforms.uNoise,
                uNoiseIntensity: this.#uniforms.uNoiseIntensity,
            },
        });

        this.#diamondsBack = new RawShaderMaterial({
            vertexShader: /* glsl */ `
        precision highp float;
        uniform mat4 projectionMatrix;
        uniform mat4 modelViewMatrix;
        uniform mat4 normalMatrix;

        attribute vec3 position;
        attribute vec3 normal;
        attribute vec2 uv;
        attribute vec2 uv1;

        varying vec2 vUv;
        varying vec2 vUv1;
        varying vec3 vNormal;
        varying vec3 vPosition;
        varying vec3 vViewPosition;

        uniform float uBackSide;

        void main() {
          vUv = uv;
          vUv1 = uv1;
          vPosition = position;
          vNormal = vec3(normalMatrix * vec4(normal, 1.));
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.);
          mvPosition.xyz *= 1. + mvPosition.xyz * .001 * uBackSide;
          vViewPosition = mvPosition.xyz;
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
            fragmentShader: /* glsl */ `
        precision highp float;
        varying vec2 vUv;
        varying vec2 vUv1;
        varying vec3 vNormal;
        varying vec3 vPosition;
        varying vec3 vViewPosition;

        uniform sampler2D uReflexion, uNoise;
        uniform float uOpacity, uLuminosity, uBackSide, uGlobalLuminosity;

        #define PI 3.14159265

        void main() {
          float frontSide = 1. - uBackSide;
          vec3 color = vec3(0.);
          vec2 dUv = abs(vUv - .5) * .1 + fract(atan(vPosition.x, vPosition.y) * .1) + vUv1 * frontSide + abs(vNormal.xy) * .01;
          float t = 0.;
          float r = .0005 + length(vNormal.xy) * .00001;
          color.r += texture2D(uReflexion, dUv + vec2(cos(t), sin(t)) * r).r;
          t += PI * 2. / 3.;
          color.g += texture2D(uReflexion, dUv + vec2(cos(t), sin(t)) * r).g;
          t += PI * 2. / 3.;
          color.b += texture2D(uReflexion, dUv + vec2(cos(t), sin(t)) * r).b;

          color += smoothstep(.0, 1., texture2D(uReflexion, reflect(normalize(vViewPosition), normalize(vNormal) * .2).rg).r) * .4;

          color *= 1. + (texture2D(uNoise, vPosition.xy * .3 + vUv1 * frontSide - abs(vNormal.xy) * .001).r - .5) * .3;
          color += smoothstep(.5, 1., color) * .2;
          // color = .15 + color * .3 * vec3(1.2, .9, .6);
          color = .05 + color * .3 * vec3(1.2, 1.1, 1.05);

          gl_FragColor = vec4(color * uLuminosity * uGlobalLuminosity, uOpacity);
        }
      `,
            uniforms: {
                uNoise: this.#uniforms.uNoise,
                uGlobalLuminosity: this.#uniforms.uGlobalLuminosity,
                uReflexion: { value: null },
                uOpacity: { value: 1 },
                uBackSide: { value: 1 },
                uLuminosity: { value: 3 },
            },
            transparent: true,
            side: 1,
        });

        this.#glowMaterial = new RawShaderMaterial({
            vertexShader: /* glsl */ `
        precision highp float;

        attribute vec3 position;
        attribute vec2 uv;

        varying vec2 vUv;

        uniform float uBackSide;

        void main() {
          vUv = uv;
          gl_Position = vec4(position, 1.);
        }
      `,
            fragmentShader: /* glsl */ `
        precision highp float;
        varying vec2 vUv;

        uniform sampler2D uDiffuse, uLast, uNoise;
        uniform vec2 uResolution;

        #define WEIGHT_1 0.1945946
        #define WEIGHT_2 0.1216216
        #define WEIGHT_3 0.054054
        #define WEIGHT_4 0.016216

        #define PI 3.14159265

        void main() {
          vec2 offset = 5. / uResolution;

          vec3 color = vec3(0.);
          #ifdef HORIZONTAL
            color += texture2D(uDiffuse, vUv + vec2(offset.x * 1., 0.)).rgb * WEIGHT_1;
            color += texture2D(uDiffuse, vUv - vec2(offset.x * 1., 0.)).rgb * WEIGHT_1;

            color += texture2D(uDiffuse, vUv + vec2(offset.x * 2., 0.)).rgb * WEIGHT_2;
            color += texture2D(uDiffuse, vUv - vec2(offset.x * 2., 0.)).rgb * WEIGHT_2;

            color += texture2D(uDiffuse, vUv + vec2(offset.x * 3., 0.)).rgb * WEIGHT_3;
            color += texture2D(uDiffuse, vUv - vec2(offset.x * 3., 0.)).rgb * WEIGHT_3;

            color += texture2D(uDiffuse, vUv + vec2(offset.x * 4., 0.)).rgb * WEIGHT_4;
            color += texture2D(uDiffuse, vUv - vec2(offset.x * 4., 0.)).rgb * WEIGHT_4;
          #else
            color += texture2D(uDiffuse, vUv + vec2(0., offset.y * 1.)).rgb * WEIGHT_1;
            color += texture2D(uDiffuse, vUv - vec2(0., offset.y * 1.)).rgb * WEIGHT_1;

            color += texture2D(uDiffuse, vUv + vec2(0., offset.y * 2.)).rgb * WEIGHT_2;
            color += texture2D(uDiffuse, vUv - vec2(0., offset.y * 2.)).rgb * WEIGHT_2;

            color += texture2D(uDiffuse, vUv + vec2(0., offset.y * 3.)).rgb * WEIGHT_3;
            color += texture2D(uDiffuse, vUv - vec2(0., offset.y * 3.)).rgb * WEIGHT_3;

            color += texture2D(uDiffuse, vUv + vec2(0., offset.y * 4.)).rgb * WEIGHT_4;
            color += texture2D(uDiffuse, vUv - vec2(0., offset.y * 4.)).rgb * WEIGHT_4;

            color = mix(color, texture2D(uLast, vUv).rgb, .3);
          #endif
          gl_FragColor = vec4(smoothstep(.0, .3, length(color)) * color * 2., 1.);
          // gl_FragColor = texture2D(uDiffuse, vUv);
        }
      `,
            uniforms: {
                uNoise: this.#uniforms.uNoise,
                uDiffuse: { value: null },
                uLast: { value: null },
                uResolution: this.#uniforms.uResolution,
            },
            // transparent: true,
            side: 0,
        });

        this.#brt1Quad = new Mesh(new PlaneGeometry(2, 2), this.#glowMaterial);
        this.#brt2Quad = new Mesh(
            new PlaneGeometry(2, 2),
            this.#glowMaterial.clone()
        );

        this.#brt1Quad.material.uniforms.uDiffuse.value = this.#brt.texture;
        this.#brt1Quad.material.uniforms.uLast.value = this.#brt1.texture;

        this.#brt2Quad.material.uniforms.uDiffuse.value = this.#brt1.texture;
        this.#brt2Quad.material.uniforms.uResolution =
            this.#uniforms.uResolution;
        this.#brt2Quad.material.defines.HORIZONTAL = 1;

        this.#diamondsFront = this.#diamondsBack.clone();
        this.#diamondsFront.side = 0;
        this.#diamondsFront.uniforms.uOpacity.value = 0.92;
        this.#diamondsFront.uniforms.uLuminosity.value = 1.2;
        this.#diamondsFront.uniforms.uBackSide.value = 0;
        this.#diamondsFront.uniforms.uGlobalLuminosity =
            this.#uniforms.uGlobalLuminosity;
        // this.#diamondsFront.blending = AdditiveBlending
    }

    on(name, callback) {
        if (!this.#events.has(name)) this.#events.set(name, []);
        this.#events.get(name).push(callback);
    }
    emit(name) {
        if (this.#events.has(name))
            this.#events.get(name).forEach((callback) => callback());
    }

    async load(baseUrl) {
        // const exrLoader = new EXRLoader();
        const textureLoader = new TextureLoader();
        const [model, envmap] = await Promise.all([
            new Promise((resolve) =>
                gltfLoader.load(`${baseUrl}ring.compressed.glb`, (model) =>
                    resolve(model)
                )
            ),
            textureLoader.load(`${baseUrl}studio_blur_small.jpg`),
        ]);

        this.model = model.scene;
        this.model.traverse((o) => {
            if (o.isMesh && o.material.name === "diamond") {
                if (this.#diamondsBack.uniforms.uReflexion.value === null) {
                    this.#diamondsBack.uniforms.uReflexion.value =
                        o.material.map;
                    this.#diamondsFront.uniforms.uReflexion.value =
                        o.material.map;
                }
                o.material = this.#diamondsBack;
                const front = o.clone();
                front.material = this.#diamondsFront;
                o.parent.add(front);
            }
        });

        this.#scene.add(this.model);
        this.#scene.environment = computeEnvmap(this.#renderer, envmap);
        this.#scene.environmentIntensity = 1.8;

        this.emit("load");
    }

    tick(et) {
        et /= 1000;
        this.#dt = Math.min(et - this.#et, 0.01);
        this.#et = et;
        this.#render();

        this.#uniforms.uTime.value = et / 1000;

        this.#lerpedMouse.lerp(this.#mouse, this.#dt);

        //   this.#camera.rotation.set(0, 0, 0);
        //   this.#camera.position.set(0, 0, 0);
        //   this.#camera.rotateY(
        //     this.trigonometricCameraPosition.theta -
        //       this.#lerpedMouse.x * this.parallaxIntensity * 0.1
        //   );
        //   this.#camera.rotateX(
        //     this.trigonometricCameraPosition.phi +
        //       this.#lerpedMouse.y * this.parallaxIntensity * 0.1
        //   );
        //   this.#camera.translateZ(this.trigonometricCameraPosition.radius);

        if (this.model) {
            this.model.position.copy(this.ringPosition);
            this.model.rotation.set(0, 0, 0);
            this.model.rotateY(this.ringRotation.theta);
            this.model.rotateX(this.ringRotation.phi);
        }
    }

    mousemove(x, y) {
        this.#mouse.x = x * 2 - 1;
        this.#mouse.y = -y * 2 + 1;
    }

    resize(width, height) {
        this.#viewport.width = width;
        this.#viewport.height = height;
        this.#viewport.ratio = width / height;
        this.#viewport.dpr = Math.min(
            width < 1024 ? 2 : 1.5,
            window.devicePixelRatio
        );

        this.#uniforms.uResolution.value.set(
            this.#viewport.width,
            this.#viewport.height
        );

        this.#renderer.setSize(width, height);
        this.#renderer.setPixelRatio(this.#viewport.dpr);

        this.#updateCamera();

        this.#rt.setSize(
            this.#viewport.width * this.#viewport.dpr,
            this.#viewport.height * this.#viewport.dpr
        );
        this.#brt1.setSize(
            this.#viewport.width * BLUR_DPR,
            this.#viewport.height * BLUR_DPR
        );
        this.#brt1.setSize(
            this.#viewport.width * BLUR_DPR,
            this.#viewport.height * BLUR_DPR
        );
        this.#brt2.setSize(
            this.#viewport.width * BLUR_DPR,
            this.#viewport.height * BLUR_DPR
        );
    }

    #updateCamera() {
        this.#renderCamera.aspect = this.#viewport.ratio;
        this.#renderCamera.fov =
            this.#baseFOV / Math.min(1, this.#viewport.ratio * 1.5);
        this.#renderCamera.updateProjectionMatrix();
    }

    #render() {
        // CAMERA UPDATES
        // ==========================================================================

        // Retrieve orbitCamera's spherical coordinates
        this.#cameraSpherical = new Spherical().setFromVector3(
            this.#orbitCamera.position
        );

        if (this.#cameraSpherical) {
            // Apply launch animation on spherical
            if (this.#launchSpherical) {
                for (let axis of ["radius", "phi", "theta"])
                    this.#cameraSpherical[axis] += this.#launchSpherical[axis];
            }

            // Apply scroll animation on spherical
            this.#cameraSpherical.radius += gsap.utils.mapRange(
                0,
                1,
                0,
                0.5,
                this.#scrollProgress
            );
            this.#cameraSpherical.phi += gsap.utils.mapRange(
                0,
                1,
                -Math.PI / 2,
                Math.PI / 2,
                this.#scrollProgress
            );
            this.#cameraSpherical.phi = Math.min(
                Math.max(0.1, this.#cameraSpherical.phi),
                Math.PI - 0.1
            );

            // Update camera position from cameraSpherical
            this.#camera.position.copy(
                this.#orbitCamera.worldToLocal(
                    new Vector3().setFromSpherical(this.#cameraSpherical)
                )
            );
            this.#camera.lookAt(0, 0, 0);
        }

        this.#controls.update();

        this.#renderer.setRenderTarget(this.#rt);
        this.#renderer.clear();
        this.#renderer.render(this.#scene, this.#renderCamera);

        this.#renderer.setRenderTarget(this.#brt);
        this.#renderer.clear();
        this.#renderer.render(this.#scene, this.#renderCamera);

        this.#renderer.setRenderTarget(this.#brt1);
        this.#renderer.clear();
        this.#renderer.render(this.#brt1Quad, this.#renderCamera);

        this.#renderer.setRenderTarget(this.#brt2);
        this.#renderer.clear();
        this.#renderer.render(this.#brt2Quad, this.#renderCamera);

        this.#renderer.setRenderTarget(null);
        this.#renderer.clear();
        this.#renderer.render(this.#quad, this.#renderCamera);
    }

    #generateNoise(size = 256) {
        const canvas = document.createElement("canvas");
        canvas.width = canvas.height = size;

        const ctx = canvas.getContext("2d");
        const image = ctx.getImageData(0, 0, size, size);

        image.data.forEach((_, i) => {
            image.data[i] = Math.round(Math.random() * 255);
        });

        ctx.putImageData(image, 0, 0);

        const texture = new CanvasTexture(canvas);
        texture.wrapS = texture.wrapT = RepeatWrapping;
        texture.magFilter = texture.minFilter = NearestFilter;
        this.#uniforms.uNoise.value = texture;
    }

    launch() {
        const duration = 4; //1.5

        this.#launchSpherical = new Spherical(0, 0, 0);
        gsap.from(this.#launchSpherical, {
            radius: 0.5,
            phi: -0.4,
            theta: 1.2,
            ease: "power4.out",
            duration,
        });

        gsap.from(this.model.position, {
            y: -0.05,
            ease: "power4.out",
            duration,
        });
    }

    destroy() {
        disposeRenderer(this.#renderer, this.#scene, {
            controls: this.#controls,
        });

        this.#scene = null;
        this.#renderer = null;
        this.#controls = null;
    }

    get fov() {
        return this.#baseFOV;
    }
    set fov(value) {
        this.#baseFOV = value;
        this.#updateCamera();
    }

    get luminosity() {
        return this.#scene.environmentIntensity;
    }
    set luminosity(value) {
        this.#uniforms.uGlobalLuminosity.value =
            this.#scene.environmentIntensity = value;
    }

    get noise() {
        return this.#uniforms.uNoiseIntensity.value;
    }
    set noise(value) {
        this.#uniforms.uNoiseIntensity.value = value;
    }

    get scrollProgress() {
        return this.#scrollProgress;
    }

    set scrollProgress(value) {
        this.#scrollProgress = value;
    }

    set autoRotate(value) {
        this.#controls.autoRotateSpeed = value;
    }

    get renderer() {
        return this.#renderer;
    }
}
export { TheRing };
